import type {
  Element,
  GroupElement,
  RectangleElement,
  CircleElement,
  TriangleElement,
  VectorElement,
  FreehandElement,
  TextElement,
  ImageElement,
  TableElement,
  LineElement,
} from '@/components/canvas/document/publicTypes';
import { drawRect } from '@/components/canvas/elements/shape/rect/export';
import { drawCircle } from '@/components/canvas/elements/shape/circle/export';
import { drawTriangle } from '@/components/canvas/elements/shape/triangle/export';
import { drawVector } from '@/components/canvas/elements/shape/vector/export';
import { drawFreehand } from '@/components/canvas/elements/shape/freehand/export';
import { drawLine } from '@/components/canvas/elements/shape/line/export';
import { drawText } from '@/components/canvas/elements/text/export';
import { drawImage } from '@/components/canvas/elements/image/export';
import { drawTable } from '@/components/canvas/elements/table/export';
import { drawGroup } from '@/components/canvas/elements/group/export';
import type { GetImageProxyUrl } from './types';
import { blurRadius } from '@/components/canvas/elements/model';

export async function drawElement(
  ctx: CanvasRenderingContext2D,
  el: Element,
  allElements: Element[],
  getImageProxyUrl?: GetImageProxyUrl,
): Promise<void> {
  if (el.interaction.visible === false) return;

  ctx.save();

  const rotateDeg = el.layout.rotation + (el.layout.rotate3d?.z ?? 0);
  if (rotateDeg) {
    const cx = el.layout.x + el.size.width / 2;
    const cy = el.layout.y + el.size.height / 2;
    ctx.translate(cx, cy);
    ctx.rotate((rotateDeg * Math.PI) / 180);
    ctx.translate(-cx, -cy);
  }

  if (el.layout.flipX || el.layout.flipY) {
    const cx = el.layout.x + el.size.width / 2;
    const cy = el.layout.y + el.size.height / 2;
    ctx.translate(cx, cy);
    ctx.scale(el.layout.flipX ? -1 : 1, el.layout.flipY ? -1 : 1);
    ctx.translate(-cx, -cy);
  }

  if (el.appearance.opacity < 1) {
    ctx.globalAlpha = el.appearance.opacity;
  }

  const blur = blurRadius(el);
  if (blur && blur > 0) {
    ctx.filter = `blur(${blur}px)`;
  }

  switch (el.type) {
    case 'rect':
      await drawRect(ctx, el as RectangleElement);
      break;
    case 'circle':
      await drawCircle(ctx, el as CircleElement);
      break;
    case 'triangle':
      await drawTriangle(ctx, el as TriangleElement);
      break;
    case 'vector':
      await drawVector(ctx, el as VectorElement);
      break;
    case 'freehand':
      drawFreehand(ctx, el as FreehandElement);
      break;
    case 'text':
      await drawText(ctx, el as TextElement);
      break;
    case 'image':
      await drawImage(ctx, el as ImageElement, getImageProxyUrl);
      break;
    case 'table':
      drawTable(ctx, el as TableElement);
      break;
    case 'line':
      await drawLine(ctx, el as LineElement);
      break;
    case 'group':
      await drawGroup(ctx, el as GroupElement, allElements, getImageProxyUrl);
      break;
  }

  ctx.restore();
}
